import { Component } from 'react';
import { RouteComponentProps } from 'react-router';
import { Spinner } from 'src/components/spiner';
import { getSource, getValueBySearch } from 'src/utils/helpers';
import { LoadDataError, registerAppError } from 'src/utils/register-app-error';
import { decryptPayload, DecryptedCallInfo, requestCall, registerParticipant, CallReason, getReasons, getWorkTimeSettings, WorkTimeSettings, SourceInterface } from 'src/api';
import { SimpleCard } from '../../components/simple-card';
import { withTranslation, WithTranslation } from 'react-i18next';
import { IntroForm } from '../../components/intro-form/intro-form';
import ParticipantInfoSourceForm from 'src/components/participant-info-source-form';

type RegisterCallProps = RouteComponentProps & WithTranslation;

interface RegisterCallPageState {
    loading: boolean;
    decryptedCallInfo: DecryptedCallInfo | null;
    error: boolean;
    reasons: CallReason[];
    workTimeInfo: WorkTimeSettings;
    currentForm: 'intro' | 'participant-info';
    isViible: boolean;
    mockSource: SourceInterface;
}

class RegisterCallPage extends Component<RegisterCallProps, RegisterCallPageState> {
    private forLegalEntities: boolean;
    private forMarket: boolean;

    constructor(props: RegisterCallProps) {
        super(props);
        const interfaceType = window.VBwidgetConfig.interfaceType;
        this.forLegalEntities = interfaceType === 'legal-entity';
        this.forMarket = interfaceType === 'market';
        this.setLanguage(props.location.search);
        this.state = {
            loading: true,
            decryptedCallInfo: null,
            error: false,
            reasons: [],
            workTimeInfo: null,
            currentForm: 'intro',
            isViible: true,
            mockSource: null,
        };
    }

    public async componentDidMount() {
        const response = await getReasons(this.forLegalEntities);
        if (response && response.status === 'OK') {
            this.setState({ reasons: response.list });
        }

        const reasonsList =
            response && response.status === 'OK' ? response.list : [];
        const reasonsField = reasonsList.map((reason, index) => {
            return {
                code: reason.code,
                isActive: true,
                selected: index === 0,
            };
        });

        const mockSource: SourceInterface = {
            ignoreWorkTime: false,
            callType: 'video',
            showFirstPage: true,
            fieldList: [
                {
                    key: 'lang',
                    type: 'text',
                    isRequired: false,
                    settings: {
                        value: 'ru',
                    }
                },
                {
                    key: 'phone',
                    type: 'tel',
                    isRequired: true,
                },
                {
                    key: 'iin',
                    type: 'number',
                    isRequired: true,
                },
                {
                    key: 'reason',
                    type: 'select',
                    isRequired: true,
                    settings: {
                        list: reasonsField,
                    },
                },
            ],
        };

        if (this.forLegalEntities) {
            mockSource.fieldList = mockSource.fieldList.filter(
                (source) => source.key === 'lang' || source.key === 'phone'
            );
        }

        if (this.forMarket) {
            mockSource.fieldList = mockSource.fieldList.filter(
                (source) => source.key === 'lang' || source.key === 'phone' || source.key === 'reason'
            );

            mockSource.fieldList.splice(mockSource.fieldList.length - 1, 0, {
                key: 'name',
                type: 'text',
                isRequired: true,
            });
        }

        this.setState({ mockSource });
        const callInfo = await this.checkPayload(this.props.location.search);
        let workTimeInfo: WorkTimeSettings = null;
        if (!callInfo || !callInfo.operator || callInfo.operator === '') {
            workTimeInfo = await getWorkTimeSettings();
        }

        this.setState({ decryptedCallInfo: callInfo, loading: false, workTimeInfo });
    }

    public async componentDidUpdate(prevProps: RegisterCallProps) {
        const search = this.props.location.search;
        if (prevProps.location.search !== search) {
            this.setState({ loading: true });
            this.setLanguage(search);
            await this.checkPayload(search);
        }
    }

    private setLanguage = (search: string) => {
        const query = new URLSearchParams(search);
        const lang = query.get('lang');
        if (lang) {
            this.props.i18n.changeLanguage(lang);
        }
    }

    private checkPayload = async (search: string) => {
        const payload = getValueBySearch(search, 'payload');
        if (!payload || payload === '') {
            this.setState({ loading: false });
            return null;
        }

        const callInfo = await decryptPayload(payload);
        if (!callInfo || callInfo.status === 'ERROR') {
            this.setState({ loading: false });
            registerAppError(LoadDataError.PayloadIsNotValid, 'Payload is not valid.');
            return null;
        }

        const isDataComplete = this.isDataComplete(callInfo.data);
        if (isDataComplete) {
            const url = await this.registerCall(callInfo.data);
            if (!url) {
                return;
            }

            this.openCallPage(url, false);
            return null;
        }

        return callInfo.data;
    }

    private onDataFilled = async (data: DecryptedCallInfo) => {
        this.setState({ decryptedCallInfo: data });
        const url = await this.registerCall(data);
        if (!url) {
            return;
        }

        this.openCallPage(url, true);
    }

    private registerCall = async (data: DecryptedCallInfo) => {
        const callHash = await this.requestCall(data);
        if (!callHash) {
            this.setState({ error: true, loading: false });
            return null;
        }

        if (window.VBwidgetConfig.useBrokerV1) {
            const url = window.VBwidgetConfig.clientAppUrl.replace('{{callHash}}', callHash);
            return url;
        }

        const participantInfo = await this.registerParticipant(callHash, data);
        if (!participantInfo || participantInfo.status === 'ERROR') {
            this.setState({ error: true, loading: false });
            return null;
        }

        return participantInfo.callUrl;
    }

    private requestCall = async (data: DecryptedCallInfo) => {
        const { operator, reason } = data;
        const source = getSource(this.props.location.search);

        let request: DecryptedCallInfo = {
            reason,
            operator,
        };

        if (window.VBwidgetConfig.useBrokerV1) {
            request = data;
        }

        const response = await requestCall({ sourceUrl: source, ...request } as any);

        if (!response || response.status === 'ERROR') {
            return null;
        }

        return response.callHash;
    }

    private registerParticipant = async (callHash: string, data: DecryptedCallInfo) => {
        const response = await registerParticipant({ ...data, callHash });
        return response;
    }

    private isDataComplete = (data: DecryptedCallInfo | null) => {
        if (!data) {
            return false;
        }

        const { iin, name, phone, reason } = data;
        if (!phone || !iin || !name || !reason) {
            return false;
        }

        return true;
    }

    private openCallPage = (url: string, isAgreementChecked: boolean) => {
        const lang = this.props.i18n.language;
        window.location.href = `${url}?lang=${lang}&agreementChecked=${isAgreementChecked}`;
    }

    private onAgreementChecked = () => {
        this.setState({ currentForm: 'participant-info' });
    }

    private onClose = () => {
        this.setState({ isViible: false });
    }

    render() {
        const { t, i18n } = this.props;
        const {
            loading,
            error,
            decryptedCallInfo,
            reasons,
            workTimeInfo,
            currentForm,
            mockSource,
        } = this.state;
        const legalEntityClassName = this.forLegalEntities
            ? 'vb-legal-entity'
            : '';
        const marketClassName = this.forMarket
            ? 'vb-market-widget'
            : '';

        if (loading) {
            return <div className='vb-w-100 vb-h-100 vb-d-flex vb-justify-content-center vb-align-items-center'>
                <Spinner colorSheme="primary"></Spinner>
            </div>;
        }

        if (workTimeInfo && !workTimeInfo.isCallPossible) {
            return <SimpleCard title={t('title')}>
                <div style={{ maxWidth: 500 }}>{i18n.language === 'kz' ? workTimeInfo.messageKz : workTimeInfo.messageRu}</div>
            </SimpleCard>;
        }

        if (currentForm === 'intro') {
            return <IntroForm
                onClose={this.onClose}
                onAgreeSubmit={this.onAgreementChecked} />;
        }

        return (
            <div className={legalEntityClassName + marketClassName}>
                <ParticipantInfoSourceForm
                    reasons={reasons}
                    showOperatorCode={true}
                    error={error}
                    onDataFilled={this.onDataFilled}
                    data={decryptedCallInfo}
                    source={mockSource}
                />
            </div>
        );
    }
}

export default withTranslation('appointment')(RegisterCallPage);
